<template>
    <section>
        <img v-if="system_info.loading || settings.loading || settings.dropbox_access_token.loading" class="image page-loading" src="@/assets/img/page-loading.gif" />
        <div v-else class="mb-6">
            <h1 class="mb-5">Digital assets</h1>
            <div v-if="settings.dropbox_access_token.value">
                <div v-if="settings.dropbox_path.value != 'default'">
                    <h3 class="mt-6">Update</h3>
                    <p>The system will update all items from Dropbox automatically every 6 hours.</p>
                    <ul>
                        <li v-if="system_info.sync_running === 0 && system_info.latest_sync"><b>Latest update:</b> {{ system_info.latest_sync }}</li>
                        <li v-if="system_info.sync_running === 1"><b>Updating digital assets from Dropbox...</b></li>
                        <li v-if="system_info.sync_running === -1"><b>Something went wrong during the latest update.</b></li>
                    </ul>
                    <button v-if="system_info.sync_running === 0 || system_info.sync_running === -1" v-on:click="synchronize()" type="button" class="btn btn-primary" v-bind:class="{load: sync_start_loading}">
                        <span v-if="system_info.sync_running === 0">Update Now</span>
                        <span v-if="system_info.sync_running === -1">Try Again</span>
                        <div class="loading"></div>
                    </button>
                    <div v-if="system_info.sync_running === 1" class="progress">
                        <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" aria-valuenow="50" aria-valuemin="0" aria-valuemax="100" style="width: 50%"></div>
                    </div>
                    <h3 class="mt-6">Usage limit</h3>
                    <p>The system contains <b>{{ system_info.total_items }}</b> items with a limit of <b>{{ system_info.items_limit }}</b> items.</p>
                    <div class="progress">
                        <div class="progress-bar" role="progressbar" v-bind:style="{width: system_info.items_usage + '%'}" v-bind:aria-valuenow="system_info.items_usage" aria-valuemin="0" aria-valuemax="100">{{ system_info.items_usage }}%</div>
                    </div>
                    <small><i>This is a soft limit. You will be contacted if you have passed your limit.</i></small>
                </div>
                <h3 class="mt-6">Dropbox Path</h3>
                <p>Choose the Dropbox path you want to synchronize your digital assets from.</p>
                <div v-if="settings.dropbox_path.loading">
                    <div class="spinner-border text-dark d-block mb-2"></div>
                    <small><i>Fetching folders from Dropbox, this can take up to 60 seconds if you have many folders.</i></small>
                </div>
                <select v-else v-model="settings.dropbox_path.value" @change="update_dropbox_path()" :disabled="system_info.sync_running === 1" class="custom-select">
                    <option selected value="default">Choose Dropbox path</option>
                    <option v-for="path in settings.dropbox_path.list" :key="path.name" :value="path.path">{{ path.name }}</option>
                </select>
                <h3 class="mt-6">Disconnect Dropbox</h3>
                <p>Disconnect your Dropbox here if you want to connect to another account.</p>
                <button v-on:click="disconnect_dropbox()" v-bind:class="{load: system_info.disconnect_running === 1}" :disabled="system_info.sync_running === 1" type="button" class="btn btn-primary">
                    Disconnect Dropbox
                    <div class="loading"></div>
                </button>
            </div>
            <div v-else>
                <h3 class="mt-6">Dropbox</h3>
                <p>Connect your Dropbox to start synchronize your digital assets.</p>
                <div>
                    <a :href="settings.dropbox_auth_url" class="btn btn-primary">Connect to Dropbox</a>
                </div>
            </div>
        </div>
        <!-- Synchronize Modal -->
        <b-modal title="Update has started" id="synchronize-modal" modal-class="message" hide-header-close ok-only ok-title="Close" centered>
            <p>The update has started and may take up to an hour to complete depending on the amount of items to synchronize from Dropbox.</p>
            <p class="mb-0"><i>You can use the system as normal in the meantime, the system will work in the background.</i></p>
        </b-modal>
    </section>
</template>

<script>
    /* eslint-disable */
    export default {
        name: 'AccountDigitalAssets',
        metaInfo: {
            title: 'Digital assets'
        },
        data () {
            return {
                sync_start_loading: false,
                system_info: {
                    interval_id: null,
                    sync_running: 0,
                    disconnect_running: 0,
                    latest_sync: false,
                    total_items: false,
                    items_limit: false,
                    items_usage: false,
                    loading: false
                },
                settings: {
                    interval_id: null,
                    dropbox_access_token: {
                        value: null,
                        loading: false
                    },
                    dropbox_path: {
                        value: null,
                        list: null,
                        loading: false
                    },
                    dropbox_auth_url: null,
                    loading: false
                }
            }
        },
        mounted () {
            this.load_system_info();
            this.load_settings();
        },
        beforeDestroy () {
            clearInterval(this.system_info.interval_id);
            clearInterval(this.settings.interval_id);
        },
        methods: {
            dropbox_access_token () {
                const code = this.$route.query.code;
                const state = this.$route.query.state;
                if (code && state) {
                    this.settings.dropbox_access_token.loading = true;
                    axios.put(process.env.VUE_APP_API_URL + '/api/settings/', {
                        code: code,
                        state: state
                    })
                    .then(response => (
                        this.settings.dropbox_access_token.value = true,
                        this.settings.dropbox_access_token.loading = false,
                        this.$router.push('/account/digital-assets'),
                        this.load_dropbox_path_list()
                    ))
                }
            },
            load_system_info () {
                this.system_info.loading = true;
                axios.get(process.env.VUE_APP_API_URL + '/api/system_info/')
                    .then(response => (
                        this.system_info.sync_running = response.data['sync_running'],
                        this.system_info.disconnect_running = response.data['disconnect_running'],
                        this.system_info.latest_sync = response.data['latest_sync'],
                        this.system_info.total_items = response.data['total_items'],
                        this.system_info.items_limit = response.data['items_limit'],
                        this.system_info.items_usage = response.data['items_usage'],
                        this.system_info.loading = false,
                        this.load_processes()
                    ))
            },
            load_settings () {
                this.settings.loading = true;
                axios.get(process.env.VUE_APP_API_URL + '/api/settings/')
                    .then(response => (
                        this.settings.dropbox_access_token.value = response.data['dropbox_access_token'],
                        this.settings.dropbox_path.value = (response.data['dropbox_path']) ? response.data['dropbox_path'] : 'default',
                        this.settings.dropbox_auth_url = response.data['dropbox_auth_url'],
                        this.settings.loading = false,
                        this.dropbox_access_token(),
                        this.load_dropbox_path_list()
                    ))
            },
            load_processes () {
                if (this.system_info.sync_running === 1) {
                    this.system_info_background_check();
                }
                if (this.system_info.disconnect_running === 1) {
                    this.settings_background_check();
                }
            },
            system_info_background_check () {
                this.system_info.interval_id = setInterval(() => {
                    if (this.system_info.sync_running === 1 || this.system_info.disconnect_running === 1) {
                        axios.get(process.env.VUE_APP_API_URL + '/api/system_info/')
                        .then(response => (
                            this.system_info.sync_running = response.data['sync_running'],
                            this.system_info.disconnect_running = response.data['disconnect_running'],
                            this.system_info.latest_sync = response.data['latest_sync'],
                            this.system_info.total_items = response.data['total_items'],
                            this.system_info.items_limit = response.data['items_limit'],
                            this.system_info.items_usage = response.data['items_usage']
                        ))
                    }
                }, 5000);
            },
            settings_background_check () {
                this.system_info.interval_id = setInterval(() => {
                    if (this.system_info.disconnect_running === 1) {
                        axios.get(process.env.VUE_APP_API_URL + '/api/settings/')
                        .then(response => (
                            this.settings.dropbox_access_token.value = response.data['dropbox_access_token'],
                            this.settings.dropbox_path.value = (response.data['dropbox_path']) ? response.data['dropbox_path'] : 'default',
                            this.settings.dropbox_auth_url = response.data['dropbox_auth_url'],
                            this.settings.loading = false
                        ))
                    }
                }, 5000);
            },
            load_dropbox_path_list () {
                if (this.settings.dropbox_access_token.value) {
                    this.settings.dropbox_path.loading = true;
                    axios.get(process.env.VUE_APP_API_URL + '/api/settings/dropbox-path-list')
                        .then(response => (
                            this.settings.dropbox_path.list = response.data,
                            this.settings.dropbox_path.loading = false
                        ))
                }
            },
            update_dropbox_path () {
                axios.put(process.env.VUE_APP_API_URL + '/api/settings/', {
                    dropbox_path: this.settings.dropbox_path.value
                })
            },
            synchronize () {
                this.sync_start_loading = true;
                axios.post(process.env.VUE_APP_API_URL + '/api/synchronize/')
                .then(response => (
                    this.$bvModal.show('synchronize-modal'),
                    this.system_info.sync_running = 1,
                    this.sync_start_loading = false,
                    this.system_info_background_check()
                ))
            },
            disconnect_dropbox () {
                if (confirm('Are you sure you want to disconnect your Dropbox account?')) {
                    this.system_info.disconnect_running = 1;
                    axios.put(process.env.VUE_APP_API_URL + '/api/settings/', {
                        disconnect_dropbox: true
                    })
                    .then(response => (
                        this.system_info_background_check(),
                        this.settings_background_check()
                    ))
                }
            },
        }
    }
</script>
